import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { DataService } from '../../services/dataService';
import {
  X, Folder, CheckCircle, AlertTriangle,
  Headphones, Database, FileText, Settings,
} from 'lucide-react';

/**
 * TranscribeModal — desktop-only audio transcription wizard.
 *
 * The user does NOT pick a CSV anymore. The modal automatically exports the
 * project's primary data (already loaded in DataQuad — could have come from
 * CSV / XLSX / DTA / SQLite / API / database) to a temp CSV, then runs the
 * sidecar against it. After Apply, the merged CSV is uploaded back as the
 * project primary via the regular upload pipeline.
 *
 * An "Advanced" escape hatch lets power users transcribe an arbitrary local
 * CSV instead of the project primary (e.g. a back-check audio file).
 *
 * Phases: 'preparing' → 'setup' → 'indexing' → 'setup' (when index lands)
 *         → 'running' → 'done' | 'error'
 *
 * Props:
 *   isOpen:           bool
 *   onClose:          () => void
 *   onComplete:       (payload) => void
 *                       payload = { outputPath, transcriptColumnsAdded, counters }
 *   projectId:        string                 used to fetch full primary data
 *   primaryData:      object | null          { file_name, total_rows, total_variables, columns, ... }
 *                                            for display info on the confirmation card
 *   xlsformData:      object | null          parsed XLSForm; if present, hints are auto-built
 *   tauriApi:         { invoke, listen }     desktop-only bridge
 *   primaryCsvPath:   string | null          [optional] manually skip prep phase if path is known
 */
const TranscribeModal = ({
  isOpen, onClose, onComplete,
  projectId, primaryData, xlsformData, tauriApi,
  primaryCsvPath = null,
}) => {
  const { isDarkMode } = useTheme();

  // The state machine. New phase: 'preparing' — runs first thing on open.
  const [phase, setPhase]               = useState('preparing');
  const [csvPath, setCsvPath]           = useState(primaryCsvPath || null);
  const [csvSource, setCsvSource]       = useState('project'); // 'project' | 'manual'
  const [mediaDir, setMediaDir]         = useState(null);
  const [audioIndex, setAudioIndex]     = useState(null);
  const [columnSel, setColumnSel]       = useState({});
  const [model, setModel]               = useState('base');
  // Ship 2: audio language defaults to project's saved audio_settings.audio_language
  // (set via AudioAudit's language picker). Falls back to 'en' if unset.
  const [language, setLanguage]         = useState('en');
  const [overwrite]                     = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [jobId, setJobId]               = useState(null);
  const [progress, setProgress]         = useState({ done: 0, total: 0 });
  const [currentCell, setCurrentCell]   = useState(null);
  const [counters, setCounters]         = useState({ done: 0, skipped: 0, missing: 0, failed: 0 });
  const [startedAt, setStartedAt]       = useState(null);
  const [logEntries, setLogEntries]     = useState([]);
  const [logExpanded, setLogExpanded]   = useState(false);
  const [errorMsg, setErrorMsg]         = useState(null);
  const [completionPayload, setCompletionPayload] = useState(null);

  // Tick to refresh elapsed seconds while running
  const [, setTick] = useState(0);
  useEffect(() => {
    if (phase !== 'running') return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  // ── Ship 2: load saved audio language so the sidecar gets the right hint ──
  useEffect(() => {
    if (!isOpen || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await DataService.getAudioSettings(projectId);
        if (!cancelled && settings?.audio_language) {
          // 'auto' → null (let Whisper detect); anything else → that code
          setLanguage(settings.audio_language === 'auto' ? '' : settings.audio_language);
        }
      } catch (e) {
        // Settings may not exist yet — keep default 'en'
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  // Output paths
  const outputCsvPath = useMemo(() => {
    if (!csvPath) return null;
    return csvPath.replace(/\.csv$/i, '_with_transcripts.csv');
  }, [csvPath]);

  // ─── CSV serialization helpers ────────────────────────────────────────────
  // Defensive: handle (a) data as array of objects vs array of arrays,
  //            (b) columns as array of strings vs array of objects with .name
  const csvEscape = (value) => {
    if (value == null) return '';
    const s = String(value);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const buildCsvFromFullData = useCallback((fullData) => {
    if (!fullData) throw new Error('No data to serialize.');
    const rows = fullData.data || fullData.rows || [];
    if (!Array.isArray(rows)) throw new Error('Project data is not an array of rows.');

    // Resolve column names — try in order:
    //   1. fullData.columns as array of strings
    //   2. fullData.columns as array of objects with .name
    //   3. Object.keys(rows[0])
    //   4. Object.keys(fullData.column_types)
    let columnNames = [];
    const cols = fullData.columns;
    if (Array.isArray(cols) && cols.length > 0) {
      columnNames = cols.map(c => (typeof c === 'string' ? c : (c?.name || c?.column || ''))).filter(Boolean);
    }
    if (columnNames.length === 0 && rows.length > 0 && rows[0] && typeof rows[0] === 'object' && !Array.isArray(rows[0])) {
      columnNames = Object.keys(rows[0]);
    }
    if (columnNames.length === 0 && fullData.column_types && typeof fullData.column_types === 'object') {
      columnNames = Object.keys(fullData.column_types);
    }
    if (columnNames.length === 0) {
      throw new Error('Could not determine column names from project data.');
    }

    const lines = [];
    lines.push(columnNames.map(csvEscape).join(','));

    for (const row of rows) {
      let cells;
      if (Array.isArray(row)) {
        // Row is a positional array; align to columnNames length
        cells = columnNames.map((_, i) => csvEscape(row[i]));
      } else if (row && typeof row === 'object') {
        // Row is an object; lookup by column name
        cells = columnNames.map(name => csvEscape(row[name]));
      } else {
        // Skip null/undefined rows
        continue;
      }
      lines.push(cells.join(','));
    }

    // Use \r\n line endings — most compatible across pandas, Excel, Stata, etc.
    return lines.join('\r\n') + '\r\n';
  }, []);

  // ─── Prepare data: fetch full primary, serialize to CSV, write to temp ──
  const prepareDataForTranscribe = useCallback(async () => {
    if (!tauriApi?.invoke) {
      throw new Error('Tauri API not available — desktop-only feature.');
    }
    if (!projectId) {
      throw new Error('No project loaded. Please load a project before transcribing.');
    }

    // 1. Fetch full primary data from the backend (works regardless of source format)
    appendLog({ kind: 'info', text: 'Fetching project primary data...' });
    const fullData = await DataService.getProjectDataFull(projectId, 'primary');
    if (!fullData || !(fullData.data || fullData.rows)) {
      throw new Error('Server returned no primary data for this project.');
    }
    const rowCount = (fullData.data || fullData.rows).length;
    appendLog({ kind: 'info', text: `Received ${rowCount.toLocaleString()} rows from server.` });

    // 2. Serialize to CSV
    const csvText = buildCsvFromFullData(fullData);
    appendLog({ kind: 'info', text: `Serialized to CSV (${(csvText.length / 1024).toFixed(1)} KB).` });

    // 3. Resolve a temp path via Rust
    const slug = (primaryData?.file_name || 'primary').replace(/\.[^.]+$/, '').slice(0, 40);
    const tempPath = await tauriApi.invoke('get_temp_csv_path', { slug });
    if (!tempPath) throw new Error('Failed to resolve temp file path.');

    // 4. Write CSV to disk
    await tauriApi.invoke('write_text_file', { path: tempPath, contents: csvText });
    appendLog({ kind: 'info', text: `Staged CSV at ${tempPath}` });

    return tempPath;
  
  }, [projectId, primaryData, tauriApi, buildCsvFromFullData]);

  // ─── Reset on open + auto-trigger prep ────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      // If a manual CSV path was supplied (legacy/advanced), skip prep entirely.
      const skipPrep = Boolean(primaryCsvPath);
      setPhase(skipPrep ? 'setup' : 'preparing');
      setCsvPath(primaryCsvPath || null);
      setCsvSource(skipPrep ? 'manual' : 'project');
      setMediaDir(null);
      setAudioIndex(null);
      setColumnSel({});
      setProgress({ done: 0, total: 0 });
      setCurrentCell(null);
      setCounters({ done: 0, skipped: 0, missing: 0, failed: 0 });
      setLogEntries([]);
      setErrorMsg(null);
      setCompletionPayload(null);
      setJobId(null);
      setShowAdvanced(false);
    }
  }, [isOpen, primaryCsvPath]);

  // Kick off prep when entering the 'preparing' phase
  useEffect(() => {
    if (!isOpen || phase !== 'preparing') return;
    let cancelled = false;
    (async () => {
      try {
        const tempPath = await prepareDataForTranscribe();
        if (cancelled) return;
        setCsvPath(tempPath);
        setCsvSource('project');
        setPhase('setup');
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(`Could not prepare project data: ${e.message || e}`);
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, phase, prepareDataForTranscribe]);

  // ─── Subscribe to sidecar events for active transcribe job ───────────────
  const unlistenRef = useRef([]);
  useEffect(() => {
    if (!isOpen || !tauriApi?.listen || !jobId) return;
    let mounted = true;

    (async () => {
      const u1 = await tauriApi.listen('transcribe-event', (e) => {
        if (!mounted) return;
        if (e.payload?.job_id !== jobId) return;
        try {
          const ev = JSON.parse(e.payload.line);
          handleSidecarEvent(ev);
        } catch (err) {
          // Non-JSON lines ignored (e.g. faster-whisper logs)
        }
      });
      const u2 = await tauriApi.listen('transcribe-stderr', (e) => {
        if (!mounted) return;
        if (e.payload?.job_id !== jobId) return;
        appendLog({ kind: 'stderr', text: (e.payload.message || '').trim() });
      });
      const u3 = await tauriApi.listen('transcribe-exit', (e) => {
        if (!mounted) return;
        if (e.payload?.job_id !== jobId) return;
        const code = e.payload.code;
        if (code !== 0 && code !== null && phase === 'running') {
          setPhase('error');
          setErrorMsg(`Sidecar exited with code ${code}`);
        }
      });
      unlistenRef.current = [u1, u2, u3];
    })();

    return () => {
      mounted = false;
      unlistenRef.current.forEach(u => { try { u(); } catch (_) {} });
      unlistenRef.current = [];
    };
  }, [isOpen, jobId, tauriApi]);

  const handleSidecarEvent = (ev) => {
    appendLog({ kind: ev.event, text: shortenEvent(ev) });

    switch (ev.event) {
      case 'plan':
        setProgress({ done: 0, total: ev.n_files || 0 });
        break;
      case 'cell_start':
        setCurrentCell({ column: ev.column, file: ev.filename, i: ev.i, total: ev.total });
        break;
      case 'cell_done':
        setProgress({ done: ev.i, total: ev.total });
        setCounters(c => ({ ...c, done: c.done + 1 }));
        break;
      case 'cell_skipped':
        setCounters(c => ({ ...c, skipped: c.skipped + 1 }));
        break;
      case 'cell_missing':
        setCounters(c => ({ ...c, missing: c.missing + 1 }));
        break;
      case 'cell_failed':
        setCounters(c => ({ ...c, failed: c.failed + 1 }));
        break;
      case 'done':
        setPhase('done');
        setCompletionPayload({
          outputPath: ev.output_csv,
          metaJsonPath: ev.meta_json || null,
          transcriptColumnsAdded: ev.transcript_columns || [],
          counters: {
            done:    ev.n_done    || 0,
            skipped: ev.n_skipped || 0,
            missing: ev.n_missing || 0,
            failed:  ev.n_failed  || 0,
            total:   ev.n_total   || 0,
          },
        });
        break;
      case 'error':
        setPhase('error');
        setErrorMsg(ev.message || 'Unknown error from sidecar');
        break;
      default:
        break;
    }
  };

  const appendLog = ({ kind, text }) => {
    if (!text) return;
    setLogEntries(entries => {
      const next = entries.length >= 500 ? entries.slice(-499) : entries;
      return [...next, { kind, text, t: Date.now() }];
    });
  };

  // ─── Pick CSV (manual / advanced mode) ────────────────────────────────────
  const handlePickCsv = async () => {
    try {
      const path = await tauriApi.invoke('pick_file', {
        title: 'Choose your survey CSV',
        extensions: ['csv'],
      });
      if (!path) return;
      setCsvPath(path);
      setCsvSource('manual');
      // If audio folder already chosen, re-index with the new CSV
      if (mediaDir) {
        await runIndex(path, mediaDir);
      }
    } catch (e) {
      setErrorMsg(`File picker failed: ${e}`);
    }
  };

  const runIndex = async (csv, media) => {
    setPhase('indexing');
    try {
      const idx = await tauriApi.invoke('run_index', {
        args: { csv_path: csv, media_dir: media },
      });
      setAudioIndex(idx);
      const sel = {};
      (idx.columns || []).forEach(c => { sel[c.column] = !c.auto_skip; });
      setColumnSel(sel);
      setPhase('setup');
    } catch (e) {
      setErrorMsg(`Audio indexing failed: ${e}`);
      setPhase('error');
    }
  };

  // ─── Pick media folder, then index it ─────────────────────────────────────
  const handlePickMediaDir = async () => {
    if (!csvPath) {
      setErrorMsg('Pick your survey CSV first.');
      return;
    }
    try {
      const path = await tauriApi.invoke('pick_folder', {
        title: 'Choose your audio media folder',
      });
      if (!path) return;
      setMediaDir(path);
      await runIndex(csvPath, path);
    } catch (e) {
      setErrorMsg(`Folder picker failed: ${e}`);
    }
  };

  // ─── Build hints from XLSForm and write to disk ───────────────────────────
  const buildHintsFromXLSForm = async () => {
    const questions = xlsformData?.parseResult?.questions;
    if (!questions || typeof questions !== 'object') return null;

    const hints = {};
    Object.entries(questions).forEach(([name, q]) => {
      const type = String(q?.type || '').trim().toLowerCase();
      if (!(type.startsWith('audio') || type === 'audio audit')) return;
      let label = String(q?.label || '').trim();
      label = label.replace(/\$\{[^}]+\}/g, '').trim();
      hints[name] = {
        label:    label ? label.slice(0, 200) : null,
        language: language || 'en',
        is_audio_question: true,
      };
    });

    if (!Object.keys(hints).length) return null;

    const hintsPath = csvPath.replace(/\.csv$/i, `_hints_${Date.now()}.json`);
    try {
      await tauriApi.invoke('write_text_file', {
        path:     hintsPath,
        contents: JSON.stringify(hints, null, 2),
      });
      return hintsPath;
    } catch (e) {
      console.warn('Hints write failed; proceeding without:', e);
      return null;
    }
  };

  // ─── Start transcription ──────────────────────────────────────────────────
  const handleStart = async () => {
    if (!csvPath) {
      setErrorMsg('Please pick your survey CSV first.');
      return;
    }
    if (!mediaDir) {
      setErrorMsg('Please pick an audio media folder first.');
      return;
    }
    if (!outputCsvPath) {
      setErrorMsg('Output path could not be determined.');
      return;
    }

    setLogEntries([]);
    setProgress({ done: 0, total: 0 });
    setCounters({ done: 0, skipped: 0, missing: 0, failed: 0 });
    setStartedAt(Date.now());
    setPhase('running');

    const hintsPath = await buildHintsFromXLSForm();
    const newJobId  = `job-${Date.now()}`;
    setJobId(newJobId);

    const selectedCols = Object.entries(columnSel)
      .filter(([, v]) => v)
      .map(([k]) => k);

    try {
      await tauriApi.invoke('run_transcribe', {
        args: {
          job_id:     newJobId,
          csv_path:   csvPath,
          media_dir:  mediaDir,
          output:     outputCsvPath,
          model,
          columns:    selectedCols.length ? selectedCols : null,
          hints_path: hintsPath,
          // empty string == "auto-detect" — pass null so the sidecar omits --language
          language:   language && language.trim() ? language : null,
          overwrite,
        },
      });
    } catch (e) {
      setPhase('error');
      setErrorMsg(`Failed to start transcription: ${e}`);
    }
  };

  const handleCancel = async () => {
    if (jobId && phase === 'running') {
      try {
        await tauriApi.invoke('cancel_transcribe', { jobId });
      } catch (e) {
        console.warn('Cancel failed:', e);
      }
    }
    onClose();
  };

  const handleApplyToProject = async () => {
    // Fire the existing onComplete (parent reads the merged CSV and uploads
    // it as the new primary data). Don't await it — same as before.
    if (completionPayload && onComplete) {
      onComplete(completionPayload);
    }

    // Side effect: also upload the meta JSON sidecar (confidence + segments)
    // so the dashboard can render confidence pills. Non-blocking: if this
    // fails for any reason (file missing, network down), we log and move on.
    // The merged CSV is already applied via onComplete above.
    if (completionPayload?.metaJsonPath && projectId && tauriApi?.invoke) {
      try {
        const bytes = await tauriApi.invoke('read_file_bytes', {
          path: completionPayload.metaJsonPath,
        });
        // Convert bytes to JSON
        let view;
        if (bytes instanceof Uint8Array) view = bytes;
        else if (Array.isArray(bytes))   view = new Uint8Array(bytes);
        else if (bytes?.buffer)          view = new Uint8Array(bytes.buffer);
        else                             view = new Uint8Array(bytes);
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(view);
        const metaDoc = JSON.parse(text);

        await DataService.saveTranscriptsMeta(projectId, metaDoc);
        console.log('[TranscribeModal] Meta JSON uploaded:', Object.keys(metaDoc.cells || {}).length, 'cells');
      } catch (e) {
        console.warn('[TranscribeModal] Meta JSON upload failed (non-fatal):', e);
      }
    }

    onClose();
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const elapsedSeconds = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
  const eta = useMemo(() => {
    if (!startedAt || !progress.total || !progress.done) return null;
    const rate = progress.done / Math.max(1, elapsedSeconds);
    const remaining = (progress.total - progress.done) / Math.max(0.01, rate);
    return Math.ceil(remaining);
  }, [progress.done, progress.total, startedAt, elapsedSeconds]);

  const progressPct = progress.total
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : 0;

  // Theme tokens harmonized with AILoadingOverlay / ProjectLoadingOverlay
  // so the transcribe experience feels native to the rest of DataQuad.
  const colors = isDarkMode
    ? {
        // Modal shell
        overlay:    'rgba(6, 8, 12, 0.72)',
        modalBg:    '#0F1115',
        modalBgGrad:'radial-gradient(ellipse 80% 60% at 50% 0%, #14181F 0%, #0F1115 60%, #0A0B0E 100%)',
        cornerA:    'rgba(56,182,255,0.06)',
        cornerB:    'rgba(245,181,71,0.05)',
        // Type
        text:       '#E7EAF0',
        textSec:    '#B8BDC9',
        textMuted:  '#6B7180',
        textDim:    '#3A3F4A',
        // Surfaces
        border:     'rgba(255,255,255,0.06)',
        borderSoft: 'rgba(255,255,255,0.04)',
        cellBg:     'rgba(255,255,255,0.02)',
        cellBdr:    'rgba(255,255,255,0.05)',
        consoleBg:  'rgba(0,0,0,0.32)',
        surface:    'rgba(255,255,255,0.03)',
        // Brand + tones
        accent:     '#38B6FF',
        accentDeep: '#1B6BE0',
        accentSoft: 'rgba(56,182,255,0.12)',
        gold:       '#F5B547',
        success:    '#10B981',
        successBg:  'rgba(16,185,129,0.12)',
        warning:    '#F59E0B',
        error:      '#EF4444',
        errorBg:    'rgba(239,68,68,0.12)',
        track:      'rgba(255,255,255,0.06)',
      }
    : {
        overlay:    'rgba(15, 17, 21, 0.55)',
        modalBg:    '#FFFFFF',
        modalBgGrad:'radial-gradient(ellipse 80% 60% at 50% 0%, #FBFCFE 0%, #F4F6FA 60%, #EDF0F5 100%)',
        cornerA:    'rgba(27,107,224,0.05)',
        cornerB:    'rgba(217,137,44,0.05)',
        text:       '#15181D',
        textSec:    '#3A3F4A',
        textMuted:  '#6B7180',
        textDim:    '#A8AEBA',
        border:     'rgba(0,0,0,0.07)',
        borderSoft: 'rgba(0,0,0,0.04)',
        cellBg:     'rgba(255,255,255,0.7)',
        cellBdr:    'rgba(0,0,0,0.06)',
        consoleBg:  'rgba(248,250,252,0.85)',
        surface:    'rgba(0,0,0,0.02)',
        accent:     '#1B6BE0',
        accentDeep: '#1453B0',
        accentSoft: 'rgba(27,107,224,0.08)',
        gold:       '#D9892C',
        success:    '#2165BE',
        successBg:  'rgba(4,120,87,0.08)',
        warning:    '#B45309',
        error:      '#DC2626',
        errorBg:    'rgba(220,38,38,0.08)',
        track:      'rgba(0,0,0,0.07)',
      };

  if (!isOpen) return null;

  // Phase chip progression — always visible in running/done phases so the
  // user sees where they are in the pipeline.
  const phaseStages = [
    { id: 'preparing', name: 'Preparing' },
    { id: 'setup',     name: 'Setup' },
    { id: 'indexing',  name: 'Scanning' },
    { id: 'running',   name: 'Transcribing' },
    { id: 'done',      name: 'Complete' },
  ];
  const phaseOrder = ['preparing', 'setup', 'indexing', 'running', 'done'];
  const currentPhaseIdx = Math.max(0, phaseOrder.indexOf(phase));
  const stageTone = (idx) => {
    if (idx < currentPhaseIdx) return 'done';
    if (idx === currentPhaseIdx) return 'active';
    return 'pending';
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: colors.overlay, backdropFilter: 'blur(8px)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && phase !== 'running') onClose(); }}
    >
      <div style={{
        width: '680px', maxWidth: '100%', maxHeight: '90vh',
        background: colors.modalBg,
        backgroundImage: colors.modalBgGrad,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', position: 'relative',
        boxShadow: isDarkMode
          ? '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.02) inset'
          : '0 32px 80px rgba(15,17,21,0.18), 0 0 0 1px rgba(255,255,255,0.5) inset',
        fontFamily: "'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        {/* Corner vignettes — soft brand glow at top-left + bottom-right */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: `
              radial-gradient(ellipse 60% 50% at 0% 0%, ${colors.cornerA}, transparent 60%),
              radial-gradient(ellipse 60% 50% at 100% 100%, ${colors.cornerB}, transparent 60%)
            `,
          }}
        />

        {/* Header — icon + title + close, plus phase chip progression */}
        <div style={{
          padding: '18px 22px 14px', borderBottom: `1px solid ${colors.border}`,
          position: 'relative', zIndex: 1,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: colors.accentSoft,
                color: colors.accent,
              }}>
                <Headphones size={15} />
              </div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text, letterSpacing: '-0.01em' }}>
                {phase === 'preparing' && 'Preparing your data'}
                {phase === 'setup'     && 'Transcribe Audio'}
                {phase === 'indexing'  && 'Scanning audio folder'}
                {phase === 'running'   && 'Transcribing'}
                {phase === 'done'      && 'Transcription complete'}
                {phase === 'error'     && 'Transcription error'}
              </div>
            </div>
            {phase !== 'running' && phase !== 'preparing' && (
              <button
                onClick={onClose}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: colors.textMuted, padding: '6px', borderRadius: '6px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Phase chip progression — only show during/after work has begun */}
          {phase !== 'error' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {phaseStages.map((stage, idx) => {
                const tone = stageTone(idx);
                return (
                  <PhaseChip key={stage.id} colors={colors} tone={tone} name={stage.name} />
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1, position: 'relative', zIndex: 1 }}>
          {phase === 'preparing' && (
            <PreparingView colors={colors} primaryData={primaryData} />
          )}
          {phase === 'setup' && (
            <SetupView
              colors={colors}
              csvPath={csvPath}
              csvSource={csvSource}
              setCsvSource={setCsvSource}
              onPickCsv={handlePickCsv}
              primaryData={primaryData}
              showAdvanced={showAdvanced}
              setShowAdvanced={setShowAdvanced}
              mediaDir={mediaDir}
              audioIndex={audioIndex}
              columnSel={columnSel}
              setColumnSel={setColumnSel}
              model={model}
              setModel={setModel}
              hasXLSForm={!!xlsformData?.parseResult?.questions}
              onPickMediaDir={handlePickMediaDir}
              errorMsg={errorMsg}
            />
          )}
          {phase === 'indexing' && (
            <CenteredSpinner colors={colors} label="Scanning your media folder..." />
          )}
          {phase === 'running' && (
            <ProgressView
              colors={colors}
              progressPct={progressPct}
              progress={progress}
              currentCell={currentCell}
              counters={counters}
              elapsedSeconds={elapsedSeconds}
              eta={eta}
              logEntries={logEntries}
              logExpanded={logExpanded}
              setLogExpanded={setLogExpanded}
            />
          )}
          {phase === 'done' && (
            <DoneView
              colors={colors}
              completionPayload={completionPayload}
              elapsedSeconds={elapsedSeconds}
              csvSource={csvSource}
              outputPath={completionPayload?.outputPath || outputCsvPath}
            />
          )}
          {phase === 'error' && (
            <ErrorView colors={colors} errorMsg={errorMsg || 'Unknown error.'} />
          )}
        </div>

        <div style={{
          padding: '14px 22px', borderTop: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '10px',
          position: 'relative', zIndex: 1,
        }}>
          {phase === 'preparing' && (
            <button onClick={onClose} style={btnStyle(colors, false)}>Cancel</button>
          )}
          {phase === 'setup' && (
            <>
              <button onClick={onClose} style={btnStyle(colors, false)}>Cancel</button>
              <button
                onClick={handleStart}
                disabled={!csvPath || !mediaDir || !audioIndex}
                style={btnStyle(colors, true, !csvPath || !mediaDir || !audioIndex)}
              >
                Start transcription
              </button>
            </>
          )}
          {phase === 'running' && (
            <button onClick={handleCancel} style={btnStyle(colors, false)}>Cancel</button>
          )}
          {phase === 'done' && (
            <>
              <button onClick={onClose} style={btnStyle(colors, false)}>
                {csvSource === 'project' ? 'Done' : 'Close'}
              </button>
              {csvSource === 'project' && (
                <button onClick={handleApplyToProject} style={btnStyle(colors, true)}>
                  Apply to project
                </button>
              )}
            </>
          )}
          {phase === 'error' && (
            <button onClick={onClose} style={btnStyle(colors, true)}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Sub-views
// ============================================================================

const SetupView = ({
  colors, csvPath, csvSource, setCsvSource, onPickCsv,
  primaryData, showAdvanced, setShowAdvanced,
  mediaDir, audioIndex, columnSel, setColumnSel,
  model, setModel, hasXLSForm, onPickMediaDir, errorMsg,
}) => {
  const totalSelected = audioIndex
    ? Object.values(columnSel).filter(Boolean).length
    : 0;
  const totalFiles = audioIndex
    ? (audioIndex.columns || [])
        .filter(c => columnSel[c.column])
        .reduce((s, c) => s + (c.n_audio_cells || 0), 0)
    : 0;

  const projectFileName = primaryData?.file_name || 'Project primary data';
  const projectRows = primaryData?.total_rows ?? primaryData?.data?.length ?? 0;
  const projectCols = primaryData?.total_variables ?? primaryData?.columns?.length ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      <Step number={1} title="Source data" colors={colors}>
        {csvSource === 'project' ? (
          <ProjectDataCard
            colors={colors}
            fileName={projectFileName}
            rows={projectRows}
            cols={projectCols}
            onSwitchToManual={() => setShowAdvanced(true)}
          />
        ) : (
          <ManualCsvCard
            colors={colors}
            csvPath={csvPath}
            onPickCsv={onPickCsv}
            onSwitchToProject={() => {
              setCsvSource('project');
              setShowAdvanced(false);
            }}
          />
        )}

        {/* Advanced toggle — only when on project source. Switch to manual mode. */}
        {csvSource === 'project' && showAdvanced && (
          <div style={{
            marginTop: '10px',
            padding: '12px 14px',
            background: colors.cellBg,
            border: `1px solid ${colors.cellBdr}`,
            borderRadius: '10px',
          }}>
            <div style={{ fontSize: '12px', color: colors.textSec, marginBottom: '8px', lineHeight: 1.5 }}>
              <strong style={{ color: colors.text }}>Advanced:</strong> transcribe a different CSV instead of
              the project primary (e.g. a back-check audio file or one-off external dataset).
              Note: results from this mode <em>cannot</em> be applied back to the project.
            </div>
            <button
              onClick={() => {
                setCsvSource('manual');
                onPickCsv();
              }}
              style={btnStyle(colors, false)}
            >
              Choose a different CSV...
            </button>
          </div>
        )}

        {csvSource === 'project' && !showAdvanced && (
          <button
            onClick={() => setShowAdvanced(true)}
            style={{
              marginTop: '6px',
              background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
              color: colors.textMuted, fontSize: '11.5px',
              fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}
          >
            <Settings size={11} />
            Advanced — use a different file
          </button>
        )}
      </Step>

      <Step number={2} title="Choose your audio media folder" colors={colors}>
        <div style={{
          padding: '12px 14px', background: colors.cellBg,
          border: `1px dashed ${colors.cellBdr}`, borderRadius: '10px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <Folder size={16} style={{ color: colors.textMuted, flexShrink: 0 }} />
          <div style={{
            flex: 1, fontSize: '13px',
            color: mediaDir ? colors.text : colors.textMuted,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {mediaDir || (csvPath ? 'No folder selected' : 'Source data not ready yet')}
          </div>
          <button
            onClick={onPickMediaDir}
            style={btnStyle(colors, false, !csvPath)}
            disabled={!csvPath}
          >
            {mediaDir ? 'Change' : 'Choose folder...'}
          </button>
        </div>
        {audioIndex && (
          <div style={{ fontSize: '11.5px', color: colors.textSec, marginTop: '6px' }}>
            Found {audioIndex.total_files} audio files referenced in CSV;{' '}
            {audioIndex.files_on_disk} present on disk.
          </div>
        )}
      </Step>

      {audioIndex && (
        <Step
          number={3}
          title={`What to transcribe (${totalSelected} columns, ${totalFiles} files)`}
          colors={colors}
        >
          <div style={{
            maxHeight: '220px', overflowY: 'auto',
            border: `1px solid ${colors.border}`, borderRadius: '8px',
          }}>
            {(audioIndex.columns || []).map((col, idx, arr) => (
              <label
                key={col.column}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px',
                  borderBottom: idx < arr.length - 1 ? `1px solid ${colors.borderSoft}` : 'none',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!columnSel[col.column]}
                  onChange={(e) => setColumnSel(s => ({ ...s, [col.column]: e.target.checked }))}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '12.5px', fontFamily: 'monospace', color: colors.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {col.column}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>
                    {col.n_audio_cells} files
                    {col.n_files_missing ? ` • ${col.n_files_missing} missing on disk` : ''}
                    {col.auto_skip ? ` • ${col.auto_skip_reason}` : ''}
                  </div>
                </div>
                {col.auto_skip && (
                  <span style={{
                    fontSize: '10px', color: colors.warning,
                    textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                    padding: '2px 8px', background: 'rgba(217,119,6,0.1)', borderRadius: '999px',
                  }}>
                    Skip
                  </span>
                )}
              </label>
            ))}
          </div>
        </Step>
      )}

      {audioIndex && (
        <Step number={4} title="Whisper model" colors={colors}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ModelOption colors={colors} selected={model === 'tiny'}
              onSelect={() => setModel('tiny')}
              label="Tiny — fastest, lower accuracy" meta="~75 MB" />
            <ModelOption colors={colors} selected={model === 'base'}
              onSelect={() => setModel('base')}
              label="Base — recommended" meta="~140 MB • good balance" recommended />
            <ModelOption colors={colors} selected={model === 'small'}
              onSelect={() => setModel('small')}
              label="Small — slower, more accurate" meta="~480 MB" />
          </div>
        </Step>
      )}

      {hasXLSForm && audioIndex && (
        <Notice colors={colors} kind="info">
          XLSForm detected — question labels will be used as Whisper prompts to
          improve transcription accuracy on technical vocabulary.
        </Notice>
      )}

      {errorMsg && <Notice colors={colors} kind="error">{errorMsg}</Notice>}
    </div>
  );
};

const CenteredSpinner = ({ colors, label }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '14px', padding: '48px 0',
  }}>
    <div style={{
      width: '40px', height: '40px',
      borderRadius: '50%',
      border: `2px solid ${colors.cellBdr}`,
      borderTopColor: colors.accent,
      borderRightColor: colors.accent,
      animation: 'tm-spin 1s linear infinite',
      boxShadow: `0 0 16px ${colors.accent}33`,
    }} />
    <div style={{ fontSize: '12.5px', color: colors.textSec, letterSpacing: '0.02em' }}>{label}</div>
    <style>{`@keyframes tm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </div>
);

const ProgressView = ({
  colors, progressPct, progress, currentCell, counters,
  elapsedSeconds, eta, logEntries, logExpanded, setLogExpanded,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

    {/* ── Now-processing — current file under the magnifying glass ── */}
    {currentCell && (
      <div style={{
        padding: '12px 14px',
        background: colors.cellBg,
        border: `1px solid ${colors.cellBdr}`,
        borderRadius: '10px',
        fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: '11.5px',
        color: colors.textSec,
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <span style={{
          width: '6px', height: '6px', borderRadius: '999px',
          background: colors.accent, boxShadow: `0 0 8px ${colors.accent}`,
          animation: 'tm-pulse 1.4s ease-in-out infinite', flexShrink: 0,
        }} />
        <span style={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.12em', fontWeight: 600 }}>
          Now
        </span>
        <span style={{ color: colors.text, fontWeight: 500 }}>{currentCell.column}</span>
        <span style={{ color: colors.textDim }}>·</span>
        <span style={{ color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentCell.file}
        </span>
      </div>
    )}

    {/* ── Progress bar with gradient fill + meta line below ── */}
    <div>
      <div style={{
        position: 'relative', height: '6px',
        background: colors.track, borderRadius: '999px', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          width: `${progressPct}%`,
          background: `linear-gradient(90deg, ${colors.accentDeep} 0%, ${colors.accent} 50%, ${colors.gold} 100%)`,
          borderRadius: '999px',
          transition: 'width 300ms ease',
          boxShadow: `0 0 12px ${colors.accent}55`,
        }} />
        {/* Shimmer sweep */}
        {progressPct < 100 && (
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: `${progressPct}%`, height: '100%',
            overflow: 'hidden', borderRadius: '999px',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, width: '40%', height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
              animation: 'tm-shimmer 2s ease-in-out infinite',
            }} />
          </div>
        )}
      </div>
      <div style={{
        marginTop: '8px', display: 'flex', justifyContent: 'space-between',
        fontSize: '11.5px', color: colors.textMuted,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em',
      }}>
        <span>
          <span style={{ color: colors.text, fontWeight: 600 }}>{progress.done}</span>
          <span style={{ color: colors.textDim }}> / </span>
          <span>{progress.total} files</span>
          <span style={{ color: colors.textDim }}> · </span>
          <span style={{ color: colors.accent, fontWeight: 600 }}>{progressPct}%</span>
        </span>
        <span>
          {fmtTime(elapsedSeconds)} elapsed
          {eta != null ? <> <span style={{ color: colors.textDim }}>·</span> ~{fmtTime(eta)} remaining</> : ''}
        </span>
      </div>
    </div>

    {/* ── Stats grid — done / skipped / missing / failed ── */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
      <StatCell colors={colors} label="Done"    value={counters.done}    of={progress.total} tone={counters.done > 0 ? 'success' : null} />
      <StatCell colors={colors} label="Skipped" value={counters.skipped} />
      <StatCell colors={colors} label="Missing" value={counters.missing} tone={counters.missing > 0 ? 'warning' : null} />
      <StatCell colors={colors} label="Failed"  value={counters.failed}  tone={counters.failed  > 0 ? 'error'   : null} />
    </div>

    {/* ── Live console — always visible, mono, color-coded ── */}
    <LiveConsole colors={colors} logEntries={logEntries} maxHeight={200} />
  </div>
);

const DoneView = ({ colors, completionPayload, elapsedSeconds, csvSource, outputPath }) => {
  const c = completionPayload?.counters || {};
  const isProject = csvSource !== 'manual';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Hero success card with subtle gradient */}
      <div style={{
        padding: '18px 20px',
        background: `linear-gradient(135deg, ${colors.successBg}, transparent)`,
        border: `1px solid ${colors.success}33`,
        borderRadius: '12px',
        display: 'flex', alignItems: 'center', gap: '14px',
      }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '50%',
          background: colors.successBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <CheckCircle size={22} style={{ color: colors.success }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 600, color: colors.text, letterSpacing: '-0.01em' }}>
            {c.done || 0} of {c.total || 0} files transcribed in {fmtTime(elapsedSeconds)}
          </div>
          <div style={{ fontSize: '12px', color: colors.textSec, marginTop: '3px' }}>
            {(completionPayload?.transcriptColumnsAdded?.length || 0)} new transcript columns added
          </div>
        </div>
      </div>

      {/* Final stats grid — same StatCells as the running view */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
        <StatCell colors={colors} label="Done"    value={c.done    || 0} of={c.total} tone={(c.done || 0) > 0 ? 'success' : null} />
        <StatCell colors={colors} label="Skipped" value={c.skipped || 0} />
        <StatCell colors={colors} label="Missing" value={c.missing || 0} tone={(c.missing || 0) > 0 ? 'warning' : null} />
        <StatCell colors={colors} label="Failed"  value={c.failed  || 0} tone={(c.failed  || 0) > 0 ? 'error'   : null} />
      </div>

      {isProject ? (
        <div style={{ fontSize: '13px', color: colors.textSec, lineHeight: 1.55 }}>
          Click <strong style={{ color: colors.text }}>Apply to project</strong> to replace your project's primary
          data with the merged file. All detectors will then run on the new
          transcript columns.
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: colors.textSec, lineHeight: 1.55 }}>
          Output written to:
          <div style={{
            marginTop: '6px', padding: '8px 10px',
            background: colors.cellBg, border: `1px solid ${colors.cellBdr}`,
            borderRadius: '8px', fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: '11.5px', color: colors.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {outputPath || '—'}
          </div>
          <div style={{ fontSize: '11.5px', color: colors.textMuted, marginTop: '6px', lineHeight: 1.5 }}>
            Manual mode: the merged CSV stays on disk. To make these transcripts part
            of a project, load this file as primary data through the regular upload flow.
          </div>
        </div>
      )}

      {(c.missing || c.failed) ? (
        <Notice colors={colors} kind="warning">
          {c.missing > 0 && `${c.missing} audio file${c.missing !== 1 ? 's' : ''} missing on disk. `}
          {c.failed  > 0 && `${c.failed} transcription${c.failed !== 1 ? 's' : ''} failed. `}
          Affected cells are flagged in the merged CSV.
        </Notice>
      ) : null}
    </div>
  );
};

const ErrorView = ({ colors, errorMsg }) => (
  <div style={{
    padding: '18px 20px',
    background: `linear-gradient(135deg, ${colors.errorBg}, transparent)`,
    border: `1px solid ${colors.error}33`, borderRadius: '12px',
    display: 'flex', alignItems: 'flex-start', gap: '14px',
  }}>
    <div style={{
      width: '40px', height: '40px', borderRadius: '50%',
      background: colors.errorBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <AlertTriangle size={20} style={{ color: colors.error }} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', letterSpacing: '-0.01em' }}>
        Something went wrong
      </div>
      <div style={{ fontSize: '12.5px', color: colors.textSec, lineHeight: 1.55, fontFamily: errorMsg.includes('\n') ? "'JetBrains Mono', ui-monospace, monospace" : 'inherit' }}>
        {errorMsg}
      </div>
    </div>
  </div>
);

// ============================================================================
// Atoms
// ============================================================================

// ── Preparing view — fullscreen-style spinner while we serialize project data ──
const PreparingView = ({ colors, primaryData }) => {
  const fileName = primaryData?.file_name || 'project primary';
  const rows = primaryData?.total_rows ?? primaryData?.data?.length ?? 0;
  const cols = primaryData?.total_variables ?? primaryData?.columns?.length ?? 0;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '14px', padding: '32px 0 20px',
    }}>
      <div style={{
        width: '40px', height: '40px',
        borderRadius: '50%',
        border: `2px solid ${colors.cellBdr}`,
        borderTopColor: colors.accent,
        borderRightColor: colors.accent,
        animation: 'tm-spin 1s linear infinite',
        boxShadow: `0 0 16px ${colors.accent}33`,
      }} />
      <div style={{ textAlign: 'center', maxWidth: '440px' }}>
        <div style={{ fontSize: '13px', color: colors.text, fontWeight: 500, marginBottom: '6px' }}>
          Exporting project data for transcription
        </div>
        <div style={{ fontSize: '11.5px', color: colors.textMuted, lineHeight: 1.55 }}>
          Staging <strong style={{ color: colors.textSec }}>{fileName}</strong> ({rows.toLocaleString()} rows · {cols.toLocaleString()} columns) as CSV.
          The original file format and source are preserved — your project is unchanged.
        </div>
      </div>
      <style>{`@keyframes tm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── Project data card — shown in Step 1 when source = 'project' ──
const ProjectDataCard = ({ colors, fileName, rows, cols, onSwitchToManual }) => (
  <div style={{
    padding: '14px 16px',
    background: colors.cellBg,
    border: `1px solid ${colors.cellBdr}`,
    borderRadius: '10px',
    display: 'flex', alignItems: 'center', gap: '12px',
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '9px',
      background: colors.accentSoft,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: colors.accent, flexShrink: 0,
    }}>
      <Database size={17} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: '13px', fontWeight: 500, color: colors.text,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {fileName}
      </div>
      <div style={{ fontSize: '11.5px', color: colors.textMuted, marginTop: '2px' }}>
        {rows.toLocaleString()} rows · {cols.toLocaleString()} columns · staged as CSV
      </div>
    </div>
    <div style={{
      padding: '4px 10px',
      background: colors.successBg,
      border: `1px solid ${colors.success}33`,
      borderRadius: '999px',
      fontSize: '10.5px', fontWeight: 600,
      color: colors.success,
      letterSpacing: '0.04em',
      flexShrink: 0,
    }}>
      READY
    </div>
  </div>
);

// ── Manual CSV card — shown in Step 1 when source = 'manual' ──
const ManualCsvCard = ({ colors, csvPath, onPickCsv, onSwitchToProject }) => (
  <div>
    <div style={{
      padding: '12px 14px',
      background: colors.cellBg,
      border: `1px dashed ${colors.cellBdr}`, borderRadius: '10px',
      display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <FileText size={16} style={{ color: colors.textMuted, flexShrink: 0 }} />
      <div style={{
        flex: 1, fontSize: '13px',
        color: csvPath ? colors.text : colors.textMuted,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {csvPath || 'No CSV selected'}
      </div>
      <button onClick={onPickCsv} style={btnStyle(colors, false)}>
        {csvPath ? 'Change' : 'Choose CSV...'}
      </button>
    </div>
    <button
      onClick={onSwitchToProject}
      style={{
        marginTop: '6px',
        background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
        color: colors.textMuted, fontSize: '11.5px',
        fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '6px',
      }}
    >
      ← Use project primary instead
    </button>
  </div>
);

const Step = ({ number, title, colors, children }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <div style={{
        width: '20px', height: '20px', borderRadius: '50%',
        background: colors.accentSoft, color: colors.accent,
        fontSize: '11px', fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {number}
      </div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>{title}</div>
    </div>
    <div style={{ paddingLeft: '28px' }}>{children}</div>
  </div>
);

const ModelOption = ({ colors, selected, onSelect, label, meta, recommended }) => (
  <label
    onClick={onSelect}
    style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
      background: selected ? colors.accentSoft : 'transparent',
      border: `1px solid ${selected ? colors.accent : colors.border}`,
      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s ease',
    }}
  >
    <div style={{
      width: '14px', height: '14px', borderRadius: '50%',
      border: `2px solid ${selected ? colors.accent : colors.textMuted}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {selected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.accent }} />}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '13px', color: colors.text }}>
        {label}
        {recommended && (
          <span style={{
            marginLeft: '6px', fontSize: '10px', color: colors.accent,
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
          }}>
            recommended
          </span>
        )}
      </div>
      <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '1px' }}>{meta}</div>
    </div>
  </label>
);

// ── Phase chip — pill with dot indicator + active/done/pending tone ────
const PhaseChip = ({ colors, tone, name }) => {
  const isActive = tone === 'active';
  const isDone   = tone === 'done';
  const dotBg    = isActive ? colors.accent : isDone ? colors.success : colors.textDim;
  const dotShadow = isActive ? `0 0 8px ${colors.accent}` : 'none';
  const txt      = isActive ? colors.text : isDone ? colors.textSec : colors.textMuted;
  const bdr      = isActive ? `${colors.accent}55` : colors.cellBdr;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      padding: '5px 11px', borderRadius: '999px',
      background: colors.cellBg, border: `1px solid ${bdr}`,
      fontSize: '11px', fontWeight: 500, letterSpacing: '0.1px',
      color: txt, transition: 'color 0.2s ease, border-color 0.2s ease',
    }}>
      <span style={{
        width: '6px', height: '6px', borderRadius: '999px',
        background: dotBg, boxShadow: dotShadow, flexShrink: 0,
        animation: isActive ? 'tm-pulse 1.4s ease-in-out infinite' : 'none',
      }} />
      <span>{name}</span>
    </div>
  );
};

// ── Stat cell — label + value + optional /of, AILoadingOverlay style ───
const StatCell = ({ colors, label, value, of, tone }) => {
  const valueColor = tone === 'success'  ? colors.success
                  : tone === 'warning'   ? colors.warning
                  : tone === 'error'     ? colors.error
                  : colors.text;
  return (
    <div style={{
      padding: '12px 14px',
      background: colors.cellBg,
      border: `1px solid ${colors.cellBdr}`,
      borderRadius: '10px',
    }}>
      <div style={{
        fontSize: '10px', color: colors.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        fontWeight: 500, marginBottom: '4px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '22px', fontWeight: 600, color: valueColor,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
        lineHeight: 1.1, display: 'flex', alignItems: 'baseline', gap: '4px',
      }}>
        {value}
        {of != null && of !== '' && (
          <span style={{
            fontSize: '13px', color: colors.textMuted, fontWeight: 400,
          }}>
            / {Number(of).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Live console — mono-font activity feed with color-coded entries ────
const LiveConsole = ({ colors, logEntries, maxHeight = 200 }) => {
  const containerRef = useRef(null);

  // Auto-scroll to bottom on new entries
  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logEntries.length]);

  const toneOf = (entry) => {
    const t = entry.text || '';
    if (entry.kind === 'stderr') return 'error';
    if (t.startsWith('✗') || t.includes('failed') || t.includes('[ERROR]')) return 'error';
    if (t.startsWith('⚠') || t.includes('missing') || t.includes('[WARN]'))  return 'warn';
    if (t.startsWith('✓') || t.includes('done'))    return 'success';
    if (t.startsWith('·') || t.includes('skip'))     return 'muted';
    return 'info';
  };

  const colorFor = (tone) => ({
    error:   colors.error,
    warn:    colors.warning,
    success: colors.success,
    muted:   colors.textMuted,
    info:    colors.textSec,
  }[tone] || colors.textSec);

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
      }}>
        <span style={{
          width: '7px', height: '7px', borderRadius: '999px',
          background: colors.success, boxShadow: `0 0 8px ${colors.success}`,
          animation: 'tm-pulse 1.4s ease-in-out infinite',
        }} />
        <span style={{
          fontSize: '10.5px', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.12em',
          color: colors.textMuted,
        }}>
          Live activity
        </span>
      </div>
      <div
        ref={containerRef}
        style={{
          maxHeight: `${maxHeight}px`, overflowY: 'auto',
          background: colors.consoleBg,
          border: `1px solid ${colors.cellBdr}`,
          borderRadius: '10px',
          padding: '10px 12px',
          fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: '11.5px',
          lineHeight: 1.55,
        }}
      >
        {logEntries.length === 0 && (
          <div style={{ color: colors.textDim, fontStyle: 'italic' }}>
            Waiting for activity...
          </div>
        )}
        {logEntries.slice(-200).map((entry, i) => {
          const tone = toneOf(entry);
          return (
            <div key={i} style={{
              display: 'flex', gap: '10px',
              color: colorFor(tone),
              padding: '1px 0',
            }}>
              {entry.t && (
                <span style={{ color: colors.textDim, flexShrink: 0 }}>
                  [{new Date(entry.t).toTimeString().slice(0, 8)}]
                </span>
              )}
              <span style={{ flex: 1, wordBreak: 'break-word' }}>{entry.text}</span>
            </div>
          );
        })}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: colors.accent, marginTop: '4px' }}>
          <span>❯</span>
          <span style={{
            display: 'inline-block', width: '7px', height: '13px',
            background: colors.accent, animation: 'tm-blink 1s steps(2) infinite',
          }} />
        </div>
      </div>
    </div>
  );
};

// Lightweight global keyframes — injected once, namespaced names
if (typeof document !== 'undefined' && !document.getElementById('tm-keyframes')) {
  const s = document.createElement('style');
  s.id = 'tm-keyframes';
  s.textContent = `
    @keyframes tm-pulse {
      0%, 100% { transform: scale(1);   opacity: 1; }
      50%      { transform: scale(1.4); opacity: 0.55; }
    }
    @keyframes tm-blink { to { opacity: 0; } }
    @keyframes tm-shimmer {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `;
  document.head.appendChild(s);
}

const Notice = ({ colors, kind = 'info', children }) => {
  const styles = {
    info:    { bg: colors.accentSoft, border: `${colors.accent}33`, fg: colors.text },
    warning: { bg: 'rgba(217,119,6,0.1)', border: `${colors.warning}33`, fg: colors.text },
    error:   { bg: colors.errorBg, border: `${colors.error}33`, fg: colors.text },
  }[kind];
  return (
    <div style={{
      padding: '10px 14px', background: styles.bg,
      border: `1px solid ${styles.border}`, borderRadius: '8px',
      fontSize: '12.5px', color: styles.fg, lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
};

const btnStyle = (colors, primary, disabled = false) => ({
  padding: '9px 16px',
  background: disabled
    ? (primary ? colors.textDim : 'transparent')
    : (primary
        ? `linear-gradient(135deg, ${colors.accentDeep}, ${colors.accent})`
        : 'transparent'),
  color: primary ? '#FFFFFF' : colors.text,
  border: primary ? 'none' : `1px solid ${colors.border}`,
  borderRadius: '9px',
  fontSize: '12.5px', fontWeight: 600,
  letterSpacing: '0.01em',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.15s ease',
  boxShadow: primary && !disabled ? `0 4px 14px ${colors.accent}33` : 'none',
  fontFamily: 'inherit',
});

const fmtTime = (totalSeconds) => {
  if (totalSeconds == null || isNaN(totalSeconds)) return '—';
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
};

const shortenEvent = (ev) => {
  switch (ev.event) {
    case 'plan':         return `plan: ${ev.n_files} files, ${ev.target_columns?.length} columns, model=${ev.model}`;
    case 'cell_start':   return `→ ${ev.column} (${ev.filename})`;
    case 'cell_done':    return `✓ ${ev.column} (${ev.seconds}s, ${ev.chars} chars)`;
    case 'cell_skipped': return `· skip ${ev.column} (${ev.reason})`;
    case 'cell_missing': return `⚠ missing ${ev.filename}`;
    case 'cell_failed':  return `✗ failed ${ev.column} (${ev.reason})`;
    case 'phase':        return `phase: ${ev.name}`;
    case 'done':         return `done: ${ev.n_done}/${ev.n_total}`;
    case 'error':        return `error: ${ev.message}`;
    default:             return JSON.stringify(ev).slice(0, 200);
  }
};

export default TranscribeModal;